import {
  Button,
  Flex,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { CModal, CModalBody, CModalHeader, CModalTitle } from "@coreui/react";

import {
  managedCompaniesByParentID,
  listCompanies,
  getCompany,
} from "graphql/queries";
import { updateManagedCompany } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import ConfirmModal from "components/utils/ConfirmModal";
import ManagedCompaniesTable from "components/companies/ManagedCompaniesTable";
import ManagedCompanyCreateForm from "./ManagedCompanyCreateForm";

const ManagedCompanies = ({ companyID, companyName, visible, onClose }) => {
  const [canCreate, canDelete] = usePermissions([
    "company:update", // TODO: Change permissions for root users
    "company:update",
  ]);
  const [visibleState, setVisibleState] = useState(visible);
  const [managedCompanies, setManagedCompanies] = useState([]);
  const [managedCompaniesLoading, setManagedCompaniesLoading] = useState(false);
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    managedCompanyId: null,
    managedCompanyName: null,
  });
  const [modalManagedCompany, setModalManagedCompany] = useState({
    visible: false,
    managedCompanyId: null,
  });

  const columnsMapped = useMemo(
    () =>
      managedCompanies.map((item) => {
        return {
          ...item,
          companyName: item.companyNameStr || <Loader />,
          actions: (
            <>
              {canDelete && (
                <Button
                  className="px-1"
                  children={strings.actions.delete}
                  variation="link"
                  size="small"
                  onClick={() => {
                    setModalDetails({
                      visible: true,
                      managedCompanyId: item.id,
                      managedCompanyName: item.companyNameStr,
                    });
                  }}
                />
              )}
            </>
          ),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [managedCompanies]
  );

  const onNext = () => {
    fetchManagedCompanies(paginationTokens.nextToken);
  };

  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchManagedCompanies(previous, false);
  };

  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchManagedCompanies(undefined, undefined, keywordSplit);
  };

  const openModal = (action, id) => {
    setVisibleState(false);
    setModalManagedCompany({
      visible: true,
      action,
      managedCompanyId: id,
    });
  };

  const closeModal = () => {
    setVisibleState(true);
    setModalManagedCompany({
      visible: false,
    });
  };

  const onSuccessAction = () => {
    closeModal();
    fetchManagedCompanies();
  };

  async function fetchCompanyName(id) {
    try {
      const companyData = await API.graphql(
        graphqlOperation(getCompany, {
          id,
        })
      );
      const companyItem = companyData.data.getCompany;
      return companyItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchCompanyName", err);
      return "";
    }
  }

  const getCompanyNames = (list) => {
    list?.forEach(async (item) => {
      const companyNameStr = await fetchCompanyName(item.childID);

      setManagedCompanies((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        updatedList[index] = {
          ...updatedList[index],
          companyNameStr,
        };

        return updatedList;
      });
    });
  };

  async function fetchManagedCompanies(token, isNext = true, keywords) {
    try {
      setManagedCompaniesLoading(true);
      const limitMapped = 10;
      let filterObjList = [];

      if (keywords) {
        const companiesData = await API.graphql(
          graphqlOperation(listCompanies, {
            filter: {
              and: keywords.map((key) => ({
                or: [
                  {
                    name: { contains: key },
                  },
                ],
              })),
            },
          })
        );
        const companiesItems = companiesData.data.listCompanies.items;

        filterObjList =
          companiesItems?.map((param) => ({
            childID: { eq: param.id },
          })) ?? [];
      }

      const managedCompaniesData = await API.graphql(
        graphqlOperation(managedCompaniesByParentID, {
          parentID: companyID,
          limit: !keywords ? limitMapped : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map(() => ({
                  or: [...filterObjList],
                })),
                deletedAt: { attributeExists: false },
              }
            : { deletedAt: { attributeExists: false } },
        })
      );
      const managedCompaniesItems =
        managedCompaniesData.data.managedCompaniesByParentID.items;
      setManagedCompanies(managedCompaniesItems);
      getCompanyNames(managedCompaniesItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken:
            managedCompaniesData.data.managedCompaniesByParentID.nextToken,
        };
      });
      setManagedCompaniesLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setManagedCompaniesLoading(false);
    } finally {
      setManagedCompaniesLoading(false);
    }
  }

  async function managedCompanyDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(updateManagedCompany, {
          input: { id, deletedAt: new Date().toISOString() },
        })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.updateManagedCompany.id;
      setManagedCompanies(
        managedCompanies.filter((item) => item.id !== deletedId)
      );
      setModalDetails({
        visible: false,
        managedCompanyId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchManagedCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setVisibleState(visible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return (
    <>
      <CModal
        size="xl"
        alignment="center"
        visible={visibleState}
        onClose={() => !modalManagedCompany.visible && onClose()}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>
            {strings.title} - {companyName}
          </CModalTitle>
        </CModalHeader>
        <CModalBody style={{ overflow: "hidden" }}>
          <ScrollView maxHeight="75vh">
            <div>
              <div className="p-3">
                <Flex className="mb-1" justifyContent="space-between">
                  <div style={styles.searchByKeyword}>
                    <SearchField
                      label={strings.table.search.label}
                      placeholder={strings.table.search.placeholder}
                      onSubmit={onKeywordSubmit}
                      onClear={onKeywordSubmit}
                    />
                  </div>
                  {canCreate && (
                    <Button
                      style={styles.buttonAdd}
                      children={strings.actions.addManagedCompany}
                      variation="primary"
                      onClick={() => openModal("create")}
                    />
                  )}
                </Flex>
                {managedCompaniesLoading ? (
                  <Loader />
                ) : (
                  <ManagedCompaniesTable
                    items={columnsMapped}
                    pagination={paginationTokens}
                    onNext={onNext}
                    onPrevious={onPrevious}
                  />
                )}
              </div>
              <ConfirmModal
                visible={modalDetails.visible}
                body={strings.formatString(
                  strings.modal.confirmDeleteQuestion,
                  {
                    name: modalDetails.managedCompanyName,
                  }
                )}
                onCancel={() =>
                  setModalDetails({
                    visible: false,
                    managedCompanyId: null,
                  })
                }
                onConfirm={() =>
                  managedCompanyDelete(modalDetails.managedCompanyId)
                }
              />
            </div>
          </ScrollView>
        </CModalBody>
      </CModal>
      {modalManagedCompany.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalManagedCompany.visible}
          onClose={closeModal}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              <>
                {strings.modalManagedCompany.addTitle} - {companyName}
              </>
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              <ManagedCompanyCreateForm
                companyID={companyID}
                onSuccess={onSuccessAction}
              />
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Empresas Administradas",
    successDeleted: "Empresa Administrada eliminada con éxito",
    actions: {
      addManagedCompany: "Agregar Empresa Administrada",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    responses: {
      errorFetch: "Error al consultar empresas administradas",
    },
    table: {
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    modal: {
      confirmDeleteQuestion:
        "¿Estás seguro de eliminar la empresa administrada {name}?",
    },
    modalManagedCompany: {
      addTitle: "Agregar Empresa Administrada",
    },
  },
});

const styles = {
  buttonAdd: { height: "fit-content" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default ManagedCompanies;
